import React from 'react';
import { confirmDialog } from '../../lib/confirm';
import { Link } from 'react-router-dom';
import { Meta, Display } from '../../components/Atoms';
import { LoadingState, ErrorState } from '../../components/states';
import { useMultipleApi } from '../../lib/api';
import ProfileShell from './ProfileShell';

/**
 * Profile Hub - 5 kart kafelków do sub-routes (Mój ogród / Profil pub /
 * Konto / Statystyki). Każda karta pokazuje 1-2 metryki z prawdziwych danych.
 */

const HUB_CARDS = [
  {
    key: 'garden', to: '/profile/garden',
    title: 'Mój ogród',
    desc: 'Lokalizacja, strefa klimatyczna, daty przymrozków',
  },
  {
    key: 'public', to: '/profile/public',
    title: 'Profil publiczny',
    desc: 'Bio, social, galeria, link do udostępnienia',
  },
  {
    key: 'account', to: '/profile/account',
    title: 'Konto',
    desc: 'Email, hasło, usunięcie konta',
  },
  {
    key: 'stats', to: '/profile/stats',
    title: 'Statystyki',
    desc: 'Plony, zadania, retencja sezonowa',
  },
];

function userInitials(user) {
  if (!user) return '?';
  const src = user.public_display_name || user.username || user.email || '';
  return src.trim().slice(0, 2).toUpperCase();
}

// ── AvatarUploader (klikalne kółko z hover overlay) ─────────────
function AvatarUploader({ user, onUpdate }) {
  const inputRef = React.useRef(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const [hover, setHover] = React.useState(false);

  const photoUrl = user?.profile_photo
    ? (user.profile_photo.startsWith('/') ? user.profile_photo : `/${user.profile_photo}`)
    : null;

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError('Plik za duży (max 5 MB).');
      setTimeout(() => setError(''), 4000);
      return;
    }
    if (!file.type.startsWith('image/')) {
      setError('Wybierz plik graficzny.');
      setTimeout(() => setError(''), 4000);
      return;
    }
    setBusy(true);
    setError('');
    try {
      const axios = (await import('../../../config/axios')).default;
      const formData = new FormData();
      formData.append('photo', file);
      await axios.post('/api/profile/photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onUpdate?.();
    } catch (err) {
      setError(err.response?.data?.error || 'Nie udało się przesłać zdjęcia.');
      setTimeout(() => setError(''), 4000);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleRemove = async (e) => {
    e?.stopPropagation();
    if (!photoUrl || busy) return;
    if (!await confirmDialog('Usunąć zdjęcie profilowe?')) return;
    setBusy(true);
    try {
      const axios = (await import('../../../config/axios')).default;
      await axios.delete('/api/profile/photo');
      onUpdate?.();
    } catch (err) {
      setError(err.response?.data?.error || 'Nie udało się usunąć.');
      setTimeout(() => setError(''), 4000);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ position: 'relative', flexShrink: 0 }}>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        aria-label={photoUrl ? 'Zmień zdjęcie profilowe' : 'Dodaj zdjęcie profilowe'}
        title={photoUrl ? 'Kliknij żeby zmienić' : 'Kliknij żeby dodać'}
        style={{
          width: 72, height: 72, borderRadius: '50%',
          background: photoUrl
            ? `url(${photoUrl}) center / cover, var(--t-sage)`
            : 'var(--t-sage)',
          color: 'var(--t-bg)',
          display: 'grid', placeItems: 'center',
          fontFamily: 'var(--t-display)', fontSize: 28, fontWeight: 400,
          padding: 0, border: 'none',
          cursor: busy ? 'wait' : 'pointer',
          position: 'relative', overflow: 'hidden',
          transition: 'transform 200ms ease',
          transform: hover && !busy ? 'scale(1.04)' : 'scale(1)',
        }}
      >
        {!photoUrl && userInitials(user)}
        {(hover || busy) && (
          <div style={{
            position: 'absolute', inset: 0,
            background: 'rgba(15, 20, 16, 0.65)',
            color: 'var(--t-text)',
            display: 'grid', placeItems: 'center',
            fontFamily: 'var(--t-mono)', fontSize: 10,
            letterSpacing: '.16em', textTransform: 'uppercase',
          }}>{busy ? '…' : (photoUrl ? 'Zmień' : '+ Foto')}</div>
        )}
      </button>
      {photoUrl && !busy && (
        <button
          type="button"
          onClick={handleRemove}
          aria-label="Usuń zdjęcie profilowe"
          title="Usuń zdjęcie"
          style={{
            position: 'absolute', top: -4, right: -4,
            width: 22, height: 22, borderRadius: '50%',
            background: 'var(--t-bg)',
            border: '1px solid var(--t-rust)',
            color: 'var(--t-rust)', cursor: 'pointer',
            fontSize: 13, lineHeight: 1, padding: 0,
            display: 'grid', placeItems: 'center',
          }}
        >×</button>
      )}
      {error && (
        <div style={{
          position: 'absolute', top: '100%', left: 0,
          marginTop: 8, padding: '6px 10px', borderRadius: 6,
          background: 'rgba(192, 106, 74, 0.10)',
          color: 'var(--t-rust)', fontSize: 11,
          whiteSpace: 'nowrap', zIndex: 5,
        }}>{error}</div>
      )}
      <input
        ref={inputRef}
        type="file"
        aria-label="Wybierz zdjęcie profilowe"
        accept="image/*"
        onChange={handleFile}
        style={{ display: 'none' }}
      />
    </div>
  );
}

function userLabel(user) {
  if (!user) return '';
  return user.public_display_name || user.username || user.email || '';
}

function seasonNumber(user) {
  if (!user?.created_at) return 1;
  const created = new Date(user.created_at);
  const now = new Date();
  return Math.max(1, now.getFullYear() - created.getFullYear() + 1);
}

function HubCard({ card, hint }) {
  return (
    <Link
      to={card.to}
      style={{
        display: 'flex', flexDirection: 'column',
        padding: '20px 22px',
        borderRadius: 14,
        background: 'var(--t-bg-elev)',
        border: '1px solid var(--t-line)',
        textDecoration: 'none',
        transition: 'transform 200ms ease, border-color 200ms ease',
        minHeight: 140,
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-3px)';
        e.currentTarget.style.borderColor = 'var(--t-sage)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.borderColor = 'var(--t-line)';
      }}
    >
      <Display size={22} style={{ display: 'block', letterSpacing: '-.02em' }}>
        {card.title}
      </Display>
      <div style={{
        fontSize: 13, color: 'var(--t-text-dim)',
        marginTop: 8, lineHeight: 1.45, flex: 1,
      }}>{card.desc}</div>
      {hint && (
        <div style={{
          marginTop: 14, paddingTop: 12,
          borderTop: '1px solid var(--t-line)',
          fontFamily: 'var(--t-mono)', fontSize: 10,
          letterSpacing: '.16em', textTransform: 'uppercase',
          color: 'var(--t-sage)',
        }}>{hint}</div>
      )}
    </Link>
  );
}

function StatTile({ label, value, unit }) {
  return (
    <div style={{
      flex: 1, minWidth: 100,
      padding: '14px 16px', borderRadius: 12,
      background: 'var(--t-bg-elev)',
      border: '1px solid var(--t-line)',
      textAlign: 'center',
    }}>
      <Meta style={{ marginBottom: 6 }}>{label}</Meta>
      <Display size={22} style={{ display: 'block' }}>{value}</Display>
      {unit && (
        <div style={{
          fontFamily: 'var(--t-mono)', fontSize: 10,
          letterSpacing: '.14em', color: 'var(--t-text-mute)',
          marginTop: 2, textTransform: 'uppercase',
        }}>{unit}</div>
      )}
    </div>
  );
}

export default function ProfileHub() {
  const { results, loading, errors, refetchAll: refetch } = useMultipleApi({
    me: '/api/auth/me',
    profile: '/api/auth/profile',
    plots: '/api/plots/all-with-details',
    tasks: '/api/tasks?limit=500',
  });

  const error = errors.me || errors.profile;
  if (error && (error.includes('autoryz') || error === 'Brak tokenu autoryzacji')) {
    return <ProfileShell><ErrorState error={{ status: 401, message: error }} /></ProfileShell>;
  }
  if (loading) return <ProfileShell><LoadingState /></ProfileShell>;

  const user = { ...(results.me?.user || {}), ...(results.profile || {}) };
  const allPlots = results.plots || [];
  const allBeds = allPlots.flatMap(p => p.beds || []);
  const allTasks = results.tasks?.data || [];
  const completedTasks = allTasks.filter(t => t.completed).length;
  const totalYieldKg = allBeds
    .filter(b => b.actual_harvest_date && b.yield_amount && b.yield_unit?.toLowerCase().includes('kg'))
    .reduce((sum, b) => sum + (parseFloat(b.yield_amount) || 0), 0);

  // Hints na kartach (krótkie metryki z prawdziwych danych)
  const hints = {
    garden: user.city
      ? `${user.city}${user.hardiness_zone ? ` · strefa ${user.hardiness_zone}` : ''}`
      : 'Dodaj lokalizację',
    public: user.public_username
      ? `gardenapp.pl/g/${user.public_username}`
      : 'Wyłączony',
    account: user.email_verified ? 'Email zweryfikowany' : 'Email niezweryfikowany',
    stats: `${allPlots.length} dz · ${allBeds.length} grz · ${completedTasks} zad`,
  };

  return (
    <ProfileShell>
      {/* Identity header */}
      <div className="t-stagger" style={{
        display: 'flex', alignItems: 'center', gap: 18,
        marginBottom: 28, paddingBottom: 24,
        borderBottom: '1px solid var(--t-line)',
      }}>
        <AvatarUploader user={user} onUpdate={refetch} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <Display size={32} style={{
            display: 'block', letterSpacing: '-.02em', lineHeight: 1,
          }}>{userLabel(user)}</Display>
          <div style={{ fontSize: 13, color: 'var(--t-text-dim)', marginTop: 6 }}>
            Sezon {seasonNumber(user)}
            {user.email && ` · ${user.email}`}
          </div>
        </div>
      </div>

      {/* Sezon w liczbach */}
      <Meta style={{ marginBottom: 12 }}>Sezon w liczbach</Meta>
      <div style={{
        display: 'flex', gap: 10, flexWrap: 'wrap',
        marginBottom: 36,
      }}>
        <StatTile label="Działki" value={allPlots.length} />
        <StatTile label="Grządki" value={allBeds.length} />
        <StatTile label="Zadania" value={completedTasks} unit="wyk." />
        <StatTile label="Plony" value={totalYieldKg.toFixed(1)} unit="kg" />
      </div>

      {/* Hub navigation */}
      <Meta style={{ marginBottom: 12 }}>Sekcje profilu</Meta>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
        gap: 14,
      }}>
        {HUB_CARDS.map(card => (
          <HubCard key={card.key} card={card} hint={hints[card.key]} />
        ))}
      </div>

      {/* Quick logout */}
      <div style={{ marginTop: 36, textAlign: 'center' }}>
        <Link
          to="/menu"
          style={{
            fontFamily: 'var(--t-mono)', fontSize: 11,
            letterSpacing: '.18em', textTransform: 'uppercase',
            color: 'var(--t-text-mute)', textDecoration: 'none',
          }}
        >Pełne menu →</Link>
      </div>
    </ProfileShell>
  );
}
